import { useMemo, useState } from 'react';
import { PaymentMethod, SortDirection, Vehicle } from '../../../../models';
import {
  isPaymentMethodExpired,
  isPaymentMethodExpiringWithinMonths,
} from '../../../../utils/dates';
import {
  CustomFormattedMessage,
  TranslationsKeys,
  useCustomIntl,
} from '../../../../i18n/i18n';
import {
  SERVICE_TO_TRANSLATION_KEY,
  VEHICLE_TYPE_TO_TRANSLATION_KEY,
} from '../../../../i18n/enumMappings';
import { useAppDispatch, useAppSelector } from '../../../../state/hooks';
import {
  getSelectedVehicleId,
  selectVehicle,
  unselectVehicle,
} from '../../state/onboardingUiSlice';
import {
  getServiceFiltersFilter,
  getCardExpirationFilter,
  setServiceFilters,
  setCardExpirationFilter,
} from '../../state/onboardingFilterSlice';
import { VehicleTableToolbar } from './VehicleTableToolbar';
import {
  UNASSIGNED_ITEMS_GROUP_ID,
  useVehicles,
} from '../../../../state/vehicles/useVehicles';
import { NoDataPlaceholder } from '../../../../components/NoDataPlaceholder';
import { RowData, Table } from '../../../../components/table/Table';
import { sortByProperty } from '@rio-cloud/rio-uikit/SortUtils';
import { MultiselectOption } from '@rio-cloud/rio-uikit/Multiselect';
import { SelectOption } from '@rio-cloud/rio-uikit/Select';
import {
  RowColoring,
  TableColumn,
} from '../../../../components/table/TableRow';
import {
  PaymentMethodState,
  ServiceType,
  VehicleType,
} from '../../../../codegen/vehicles';
import { VehicleRepresentation } from '../../../../components/dataRepresentation/VehicleRepresentation';
import { VehicleGroupsRepresentation } from '../../../../components/dataRepresentation/VehicleGroupsRepresentation';
import { useLocale } from '../../../../i18n/locale';
import { UserManualLink } from '../../../../components/dialog/userManualDialog/UserManualLink';
import { userManualDialogChapters } from '../../../../state/dialogs/chapters';
import { ServiceRepresentation } from '../../../../components/dataRepresentation/ServiceRepresentation';
import { ServiceCardListRepresentation } from '../../../../components/dataRepresentation/ServiceCardListRepresentation';

type VehicleRow = {
  name: string;
  vin: string;
  type: VehicleType;
  card: PaymentMethod[];
  brand: string;
  groups: string[];
  services: ServiceType[];
};

type SortingState = {
  sortBy?: keyof VehicleRow;
  sortDirection: SortDirection;
};

export enum CardExpirationFilter {
  Expired = 'expired',
  CurrentMonth = 'currentMonth',
  NextMonth = 'nextMonth',
  TwoMonths = 'twoMonths',
}

export enum ServiceFilterType {
  NoServices = 'noServices',
}

const MAX_NUMBER_OF_CARDS_TO_SHOW = 2;

export const VehicleTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const intl = useCustomIntl();
  const { locale } = useLocale();
  const { vehicles, error, groups: vehicleGroups } = useVehicles();
  const selectedVehicleId = useAppSelector(getSelectedVehicleId);
  const reduxServiceFilters = useAppSelector(getServiceFiltersFilter);
  const reduxCardExpirationFilter = useAppSelector(getCardExpirationFilter);

  const [sorting, setSorting] = useState<SortingState>({
    sortDirection: SortDirection.ASCENDING,
  });

  const getServiceTypes = (v: Vehicle) => {
    return v.serviceActivations.map((s) => s.serviceType);
  };

  const handleVehicleClicked = (id: string) => {
    if (selectedVehicleId === id) {
      dispatch(unselectVehicle());
    } else {
      const vehicle = vehicles.find((v) => v.id === id);
      if (vehicle) {
        dispatch(selectVehicle(vehicle.id));
      }
    }
  };

  const handleSearchBarValueChanged = (value: string) => {
    setFiltering({ ...filtering, searchBarValue: value });
  };

  const handleGroupsFilterValueChanged = (selected: string[]) => {
    const filter = filtering.groupFilterOptions;
    filter.forEach((f) => {
      f.selected = selected.includes(f.id);
    });
    setFiltering({ ...filtering, groupFilterOptions: filter });
  };

  const handleSortChanged = (
    sortBy: keyof VehicleRow,
    sortDirection: SortDirection,
  ) => {
    setSorting((prevSorting) => ({
      ...prevSorting,
      sortBy,
      sortDirection,
    }));
  };

  const handleServiceFilterChanged = (
    selected: (ServiceType | ServiceFilterType)[],
  ) => {
    dispatch(setServiceFilters(selected));
  };

  const handleCardExpirationFilterChanged = (
    selected?: CardExpirationFilter,
  ) => {
    dispatch(setCardExpirationFilter(selected));
  };

  const initialFilteringState = {
    searchBarValue: '',
    groupFilterOptions: vehicleGroups.map((g) => {
      return {
        id: g.name,
        label: g.name,
        selected: false,
      };
    }),
  };

  const [filtering, setFiltering] = useState(initialFilteringState);

  const filterBySearchBarValue = (vehicle: Vehicle) => {
    const filterOn = [
      vehicle.vin,
      vehicle.name,
      intl.formatMessage({
        id: VEHICLE_TYPE_TO_TRANSLATION_KEY[vehicle.type],
      }),
      vehicle.brand,
      ...vehicle.groups.map((g) => g.name),
      ...getServiceTypes(vehicle).map((service) =>
        intl.formatMessage({ id: SERVICE_TO_TRANSLATION_KEY[service] }),
      ),
    ];
    return filterOn.some((value) =>
      new RegExp(filtering.searchBarValue, 'i').test(value),
    );
  };

  const filterBySelectedGroups = (vehicle: Vehicle) => {
    const groups = filtering.groupFilterOptions
      .filter((f) => f.selected)
      .map((g) => g.id);

    return (
      !groups.length || vehicle.groups.some((g) => groups.includes(g.name))
    );
  };

  const filterByServiceType = (vehicle: Vehicle) => {
    if (reduxServiceFilters.length === 0) return true;

    const vehicleServices = getServiceTypes(vehicle);

    return reduxServiceFilters.some((filter) => {
      if (filter === ServiceFilterType.NoServices) {
        return vehicleServices.length === 0;
      }
      // Filter is a service type
      return vehicleServices.includes(filter as ServiceType);
    });
  };

  const cardMatchesExpirationFilter = (expiryString?: string): boolean => {
    if (!expiryString) return false;

    switch (reduxCardExpirationFilter) {
      case CardExpirationFilter.Expired:
        return isPaymentMethodExpired(expiryString);
      case CardExpirationFilter.CurrentMonth:
        return isPaymentMethodExpiringWithinMonths(expiryString, 0);
      case CardExpirationFilter.NextMonth:
        return isPaymentMethodExpiringWithinMonths(expiryString, 1);
      case CardExpirationFilter.TwoMonths:
        return isPaymentMethodExpiringWithinMonths(expiryString, 2);
      default:
        return false;
    }
  };

  const filterByCardExpiration = (vehicle: Vehicle) => {
    if (!reduxCardExpirationFilter) return true;

    // Check if any card in this vehicle matches the expiration filter
    return vehicle.paymentMethods.some((method) =>
      cardMatchesExpirationFilter(method.expiry),
    );
  };

  const filteredVehicles = useMemo(() => {
    return vehicles
      .filter(filterBySearchBarValue)
      .filter(filterBySelectedGroups)
      .filter(filterByServiceType)
      .filter(filterByCardExpiration);
  }, [
    filtering,
    reduxServiceFilters,
    reduxCardExpirationFilter,
    vehicles,
    locale,
  ]);

  const renderVehicleName = (vehicle: VehicleRow) => {
    return (
      <VehicleRepresentation
        vehicleType={vehicle.type}
        vehicleName={vehicle.name}
      />
    );
  };

  const renderVehicleGroups = (vehicle: VehicleRow) => {
    return <VehicleGroupsRepresentation vehicleGroups={vehicle.groups} />;
  };

  const renderVehicleConnectedCards = (vehicle: VehicleRow) => {
    return (
      <ServiceCardListRepresentation
        serviceCards={vehicle.card}
        maxNumberOfCardsToShow={MAX_NUMBER_OF_CARDS_TO_SHOW}
      />
    );
  };

  const renderAllVehicleServices = (vehicle: VehicleRow) => {
    const services = Object.values(ServiceType);
    return (
      <div className={'display-flex flex-row gap-15'}>
        {services.map((service) => {
          return (
            <div key={service}>
              <ServiceRepresentation
                serviceType={service}
                showServiceName={false}
                isActive={vehicle.services.includes(service)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const getLabel = (id: TranslationsKeys) => {
    return intl.formatMessage({
      id,
    });
  };

  const columns: TableColumn<VehicleRow>[] = [
    {
      id: 'name',
      label: getLabel('onboarding.vehicleTable.header.name'),
      renderer: renderVehicleName,
    },
    {
      id: 'groups',
      label: getLabel('onboarding.vehicleTable.header.groups'),
      renderer: renderVehicleGroups,
    },
    {
      id: 'vin',
      label: getLabel('onboarding.vehicleTable.header.vin'),
      renderer: (vehicle: VehicleRow) => (
        <div className="ellipsis-1">{vehicle.vin}</div>
      ),
    },
    {
      id: 'card',
      label: getLabel('onboarding.vehicleTable.header.cards'),
      renderer: renderVehicleConnectedCards,
    },
    {
      id: 'services',
      label: (
        <span className="display-flex align-items-center">
          <span className="ellipsis-1">
            <CustomFormattedMessage id="onboarding.vehicleTable.header.services" />
          </span>
          <UserManualLink
            chapter={userManualDialogChapters.activatingServices}
          />
        </span>
      ),
      renderer: renderAllVehicleServices,
    },
  ];

  const serviceFilterOptions: MultiselectOption[] = [
    {
      id: ServiceFilterType.NoServices,
      label: getLabel(
        'onboarding.vehicleTable.toolbar.serviceFilter.noServices',
      ),
      selected: reduxServiceFilters.includes(ServiceFilterType.NoServices),
    },
    ...Object.entries(SERVICE_TO_TRANSLATION_KEY).map(
      ([serviceKey, translationKey]) => ({
        id: serviceKey,
        label: getLabel(translationKey as TranslationsKeys),
        selected: reduxServiceFilters.includes(serviceKey as ServiceType),
      }),
    ),
  ];

  const cardExpirationFilterOptions: SelectOption[] = [
    {
      id: CardExpirationFilter.Expired,
      label: getLabel(
        'onboarding.vehicleTable.toolbar.cardExpirationFilter.expired',
      ),
      selected: reduxCardExpirationFilter === CardExpirationFilter.Expired,
    },
    {
      id: CardExpirationFilter.CurrentMonth,
      label: getLabel(
        'onboarding.vehicleTable.toolbar.cardExpirationFilter.currentMonth',
      ),
      selected: reduxCardExpirationFilter === CardExpirationFilter.CurrentMonth,
    },
    {
      id: CardExpirationFilter.NextMonth,
      label: getLabel(
        'onboarding.vehicleTable.toolbar.cardExpirationFilter.nextMonth',
      ),
      selected: reduxCardExpirationFilter === CardExpirationFilter.NextMonth,
    },
    {
      id: CardExpirationFilter.TwoMonths,
      label: getLabel(
        'onboarding.vehicleTable.toolbar.cardExpirationFilter.twoMonths',
      ),
      selected: reduxCardExpirationFilter === CardExpirationFilter.TwoMonths,
    },
  ];

  const mapVehicleToRow = (v: Vehicle): RowData<VehicleRow> => {
    const hasFailedPaymentMethods = v.paymentMethods.some(
      (method) => method.state === PaymentMethodState.OnboardingFailure,
    );
    return {
      key: v.id,
      name: v.name,
      vin: v.vin,
      type: v.type,
      card: v.paymentMethods,
      brand: v.brand,
      groups: v.groups
        .filter((g) => g.id !== UNASSIGNED_ITEMS_GROUP_ID)
        .map((g) => g.name),
      services: getServiceTypes(v),
      coloring: hasFailedPaymentMethods ? RowColoring.DANGER : undefined,
    };
  };

  const rowData = filteredVehicles.map((v) => mapVehicleToRow(v));

  const sortedRowData = sorting.sortBy
    ? sortByProperty(rowData, sorting.sortBy, sorting.sortDirection)
    : rowData;

  if (error) {
    return <NoDataPlaceholder />;
  }

  return (
    <div className="display-flex flex-column height-100pct">
      <VehicleTableToolbar
        groupFilterOptions={filtering.groupFilterOptions}
        onGroupFilterChanged={handleGroupsFilterValueChanged}
        serviceFilterOptions={serviceFilterOptions}
        onServiceFilterChanged={handleServiceFilterChanged}
        cardExpirationFilterOptions={cardExpirationFilterOptions}
        onCardExpirationFilterChanged={handleCardExpirationFilterChanged}
        searchBarValue={filtering.searchBarValue}
        onSearchBarValueChanged={handleSearchBarValueChanged}
      />
      <div
        className="table-responsive overflow-y-auto "
        data-testid="onboarding-body-vehicle-table"
      >
        <Table
          sorting={{
            sortBy: sorting.sortBy,
            sortDirection: sorting.sortDirection,
            onSortChanged: handleSortChanged,
          }}
          columns={columns}
          rowData={sortedRowData}
          onRowClicked={(key) => {
            handleVehicleClicked(key);
          }}
          highlightedRow={selectedVehicleId}
        />
      </div>
    </div>
  );
};
